import { CommonModule, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';

import { Customer360Service } from './services/customer-360.service';
import { Customer360Bundle } from './models/customer-360.types';
import { G2ChartComponent } from './components/g2-chart/g2-chart.component';

/**
 * Customer 360 — analytics-only embeddable panel.
 *
 * After splitting custom fields, photos, and communications into their
 * own tabs, this component is now strictly the "dashboard" view: a row
 * of headline KPIs plus a 2×3 chart grid backed by the `/360` bundle.
 * Visual language is flat and calm to match the user-overview panel:
 * bordered KPI tiles and chart cards, no shadows, no colored stripes or
 * hover lift. Per-tile colour survives only as the icon glyph.
 *
 * Kept decoupled from `NZ_DRAWER_DATA` so it can live inside the legacy
 * `UserViewComponent` tabset as well as the standalone `Customer360Drawer`.
 */
@Component({
  selector: 'beautyapp-customer-360-content',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DecimalPipe,
    NzSpinModule,
    NzEmptyModule,
    NzIconModule,
    G2ChartComponent,
  ],
  template: `
    <nz-spin [nzSpinning]="loading()" nzSize="large">
      <div class="c360" *ngIf="bundle() as b; else emptyTpl">
        <!-- KPI hero strip -->
        <section class="hero">
          <div class="hero-tile tile-primary">
            <div class="tile-icon">
              <span nz-icon nzType="calendar" nzTheme="outline"></span>
            </div>
            <div class="tile-body">
              <div class="tile-label">სულ ვიზიტი</div>
              <div class="tile-value">{{ b.summary.totalVisits }}</div>
            </div>
          </div>
          <div class="hero-tile tile-success">
            <div class="tile-icon">
              <span nz-icon nzType="dollar" nzTheme="outline"></span>
            </div>
            <div class="tile-body">
              <div class="tile-label">სულ დახარჯული</div>
              <div class="tile-value">
                {{ b.summary.totalSpent | number : '1.0-2' }} ₾
              </div>
            </div>
          </div>
          <div class="hero-tile tile-info">
            <div class="tile-icon">
              <span nz-icon nzType="line-chart" nzTheme="outline"></span>
            </div>
            <div class="tile-body">
              <div class="tile-label">საშ. ჩეკი</div>
              <div class="tile-value">
                {{ b.summary.averageCheck | number : '1.0-2' }} ₾
              </div>
            </div>
          </div>
          <div class="hero-tile tile-warn">
            <div class="tile-icon">
              <span nz-icon nzType="clock-circle" nzTheme="outline"></span>
            </div>
            <div class="tile-body">
              <div class="tile-label">ბოლო ვიზიტი</div>
              <div class="tile-value">{{ b.summary.lastVisit || '—' }}</div>
            </div>
          </div>
          <div class="hero-tile tile-muted">
            <div class="tile-icon">
              <span nz-icon nzType="star" nzTheme="outline"></span>
            </div>
            <div class="tile-body">
              <div class="tile-label">პირველი ვიზიტი</div>
              <div class="tile-value">{{ b.summary.firstVisit || '—' }}</div>
            </div>
          </div>
        </section>

        <!-- Analytics chart grid -->
        <section class="charts">
          <div class="section-head">
            <h3>ანალიტიკა</h3>
            <span class="section-sub">ვიზიტების და ხარჯის სრული სურათი</span>
          </div>
          <div class="chart-grid">
            <div class="chart-card">
              <div class="chart-title">კვირის დღის მიხედვით</div>
              <beautyapp-g2-chart
                type="column"
                [data]="dayOfWeekData()"
                [config]="{
                  xField: 'label',
                  yField: 'count',
                  columnWidthRatio: 0.6
                }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">საათის მიხედვით</div>
              <beautyapp-g2-chart
                type="column"
                [data]="hourData()"
                [config]="{ xField: 'hour', yField: 'count' }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">ბოლო 12 თვე — ვიზიტები</div>
              <beautyapp-g2-chart
                type="line"
                [data]="monthData()"
                [config]="{ xField: 'month', yField: 'visits', smooth: true }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">ხარჯი დროში</div>
              <beautyapp-g2-chart
                type="dual"
                [data]="spendingDual()"
                [config]="spendingDualCfg"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">თანამშრომლების მიხედვით</div>
              <beautyapp-g2-chart
                type="bar"
                [data]="employeeData()"
                [config]="{ xField: 'visits', yField: 'employeeFullName' }"
              ></beautyapp-g2-chart>
            </div>
            <div class="chart-card">
              <div class="chart-title">ტოპ სერვისები</div>
              <beautyapp-g2-chart
                type="pie"
                [data]="servicesData()"
                [config]="{
                  angleField: 'count',
                  colorField: 'serviceName',
                  radius: 0.8,
                  innerRadius: 0.5
                }"
              ></beautyapp-g2-chart>
            </div>
          </div>
        </section>
      </div>
      <ng-template #emptyTpl>
        <div *ngIf="!loading()"><nz-empty></nz-empty></div>
      </ng-template>
    </nz-spin>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .c360 {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 4px 4px 24px;
      }

      /* KPI hero strip -------------------------------------------------- */
      .hero {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 12px;
      }
      .hero-tile {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 18px;
        border-radius: 13px;
        background: #fff;
        border: 1px solid #eef0f3;
      }
      .tile-icon {
        width: 40px;
        height: 40px;
        display: grid;
        place-items: center;
        border-radius: 10px;
        font-size: 18px;
        background: #f1f5f9;
        color: currentColor;
      }
      .tile-icon span {
        color: inherit;
      }
      .tile-body {
        display: flex;
        flex-direction: column;
      }
      .tile-label {
        font-size: 12px;
        color: #667085;
        letter-spacing: 0.2px;
      }
      .tile-value {
        font-size: 20px;
        font-weight: 650;
        color: #101828;
        margin-top: 2px;
        line-height: 1.2;
      }

      .tile-primary {
        color: #4f46e5;
      }
      .tile-success {
        color: #059669;
      }
      .tile-info {
        color: #0284c7;
      }
      .tile-warn {
        color: #d97706;
      }
      .tile-muted {
        color: #6b7280;
      }

      /* Charts ---------------------------------------------------------- */
      .charts {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .section-head {
        display: flex;
        align-items: baseline;
        gap: 10px;
      }
      .section-head h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 650;
        color: #101828;
      }
      .section-sub {
        color: #667085;
        font-size: 12px;
      }

      .chart-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }
      .chart-card {
        background: #fff;
        border: 1px solid #eef0f3;
        border-radius: 13px;
        padding: 16px 16px 8px;
        min-height: 280px;
        display: flex;
        flex-direction: column;
      }
      .chart-title {
        font-weight: 600;
        margin-bottom: 10px;
        color: #344054;
        font-size: 13px;
        letter-spacing: 0.1px;
      }
      @media (max-width: 960px) {
        .chart-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class Customer360ContentComponent {
  /** Customer id to load the 360 bundle for. Reloads on change. */
  userId = input.required<string>();

  private api = inject(Customer360Service);
  private msg = inject(NzMessageService);

  loading = signal(true);
  bundle = signal<Customer360Bundle | null>(null);

  dayOfWeekData = computed(() => this.bundle()?.byDayOfWeek ?? []);
  hourData = computed(() => this.bundle()?.byHourOfDay ?? []);
  monthData = computed(() => this.bundle()?.byMonth ?? []);
  employeeData = computed(() => this.bundle()?.byEmployee ?? []);
  servicesData = computed(() => this.bundle()?.services ?? []);

  spendingDual = computed(() => {
    const series = this.bundle()?.spending?.series ?? [];
    return [series, series];
  });

  spendingDualCfg = {
    xField: 'period',
    yField: ['spent', 'visits'],
    geometryOptions: [
      { geometry: 'line', smooth: true },
      { geometry: 'line', smooth: true },
    ],
  };

  constructor() {
    effect(() => {
      const id = this.userId();
      if (id) this.load(id);
    });
  }

  private load(id: string): void {
    this.loading.set(true);
    this.api.get360(id).subscribe({
      next: (b: Customer360Bundle) => {
        this.bundle.set(b);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.msg.error('მონაცემების ჩატვირთვა ვერ მოხერხდა');
      },
    });
  }
}
